import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { THighlightsResponse } from "domain/entities/highlights-reponse";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { createBlobDownloader } from "../../../utils/files/downloadBlob";

type THighLightsProviderProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource: IReportsRepository;
};

export type HighlightsState = {
  requestParamsIndex: number;
  highlightsData?: THighlightsResponse;
  metrics?: TFilterSelectOption[];
  sources?: TFilterSelectOption[];
  reportsDatasource?: IReportsRepository;
  downloadHighlightsCSV: () => Promise<any>;
  downloadingReports?: boolean;
  showCompleteHighlights: boolean;
  isLoading: boolean;
};

type TFilterSelectOption = {
  value?: string | number;
  label?: string;
};

const initContext: HighlightsState = {
  requestParamsIndex: 0,
  downloadHighlightsCSV: () => Promise.resolve(),
  showCompleteHighlights: false,
  isLoading: true,
};
export const HighlightsContext = createContext(initContext);

export const HighlightsProvider = ({
  requestParamsIndex,
  children,
  reportsDatasource,
}: THighLightsProviderProps) => {
  const [showCompleteHighlights, setshowCompleteHighlights] =
    useState<boolean>(false);
  const [highlightsData, setHighlightData] = useState<THighlightsResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [metrics, setMetrics] = useState<TFilterSelectOption[]>([
    {
      label: "Campaign",
      value: 1,
    },
  ]);
  const [sources, setSources] = useState<TFilterSelectOption[]>([
    {
      label: "Visitors",
      value: 1,
    },
  ]);

  const { requestParams } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  useEffect(() => {
    setIsLoading(true);
    reportsDatasource
      .getHighlights(currentRequestParams)
      .then(setHighlightData)
      .then(() => setIsLoading(false));
  }, [currentRequestParams]);

  const [downloadingReports, setDownloadingReports] = useState<boolean>(false);

  const downloadHighlightsCSV = useCallback(() => {
    setDownloadingReports(true);
    return reportsDatasource
      .exportHighlightsCSV(currentRequestParams)
      .then(createBlobDownloader("highlights.zip"))
      .finally(() => setDownloadingReports(false));
  }, [currentRequestParams]);

  useEffect(() => {
    setshowCompleteHighlights(currentRequestParams.PortfolioId != null);
  }, [currentRequestParams.PortfolioId]);

  useEffect(() => {
    if (showCompleteHighlights) {
      setMetrics(
        highlightsData?.topExperienceMetrics?.map(
          (item: { id: number; description: string }) => ({
            value: item.id,
            label: item.description,
          })
        )
      );

      setSources(
        highlightsData?.topExperienceSources?.map(
          (item: { id: number; description: string }) => ({
            value: item.id,
            label: item.description,
          })
        )
      );
    }
  }, [highlightsData]);

  return (
    <HighlightsContext.Provider
      value={{
        requestParamsIndex,
        highlightsData,
        metrics,
        sources,
        reportsDatasource,
        downloadHighlightsCSV,
        downloadingReports,
        showCompleteHighlights,
        isLoading,
      }}
    >
      {children}
    </HighlightsContext.Provider>
  );
};
